import enviro from 'enviro';
import { createTracker } from 'usage-tracker-public';
import legoEvents from '../../events.yaml';
import { getUtk } from '../experiments/getUtk';
import { getIsPostVerification } from '../routing/getIsPostVerification';
import AnonymousUsageTrackerContainer from './AnonymousUsageTrackerContainer';
import { createFreshUuid } from './createUuid';
import { getInitialTrackerProperties } from './getInitialTrackerProperties';
import { getTrackerHstc } from './getTrackerHstc';
import { hasAcceptedCookies } from './hasAcceptedCookies';
import { UsageTrackingContainerLogger as LOG } from './UsageTrackingContainerLogger';
import { checkUrlForUUID } from './uuidUtils';
export const getPublicTracker = () => {
  return createTracker({
    events: legoEvents,
    properties: Object.assign({}, getInitialTrackerProperties(), {
      trackerType: 'xhr-request'
    }),
    debug: () => !!enviro.debug('usage-tracker'),
    preserveTrackerProperties: true,
    lastKnownEventProperties: ['screen'],
    isExternalHost: false,
    isBeforeUnload: false
  });
};
/**
 * Exported only for testing.
 */

export const getUsageTrackerContainer = () => ({
  areCookiesAccepted: false,
  isDeviceIdProvided: false,
  isEmailProvided: getIsPostVerification() && !checkUrlForUUID(),
  isPortalIdProvided: false,
  identifiableTrackingEnabled: false,
  queue: [],
  TRACKER: getPublicTracker(),

  getTracker() {
    return this.areCookiesAccepted && !checkUrlForUUID() || this.isPortalIdProvided ? this.TRACKER : AnonymousUsageTrackerContainer.get();
  },

  get() {
    return this.TRACKER;
  },

  hasQueuedEvents() {
    return this.queue.length;
  },

  shouldQueue() {
    return !this.identifiableTrackingEnabled;
  },

  getBeaconTracker() {
    return this.get().clone({
      isBeforeUnload: true,
      properties: {
        trackerType: 'beacon'
      }
    });
  },

  getHttpTracker() {
    return this.get();
  },

  queueEvent({
    eventName,
    properties = {},
    sendAsBeacon = false
  }) {
    LOG.queue(eventName, properties, sendAsBeacon);
    this.queue.push({
      eventName,
      properties,
      sendAsBeacon
    });
  },

  track(eventName, properties = {}, sendAsBeacon = false) {
    if (this.shouldQueue()) {
      this.queueEvent({
        eventName,
        properties,
        sendAsBeacon
      });
      return;
    }

    LOG.track(eventName, properties, sendAsBeacon);

    this._track({
      eventName,
      properties,
      sendAsBeacon
    });
  },

  drainQueue() {
    if (this.hasQueuedEvents()) {
      LOG.drainQueue(this.queue);
    }

    while (this.queue.length > 0) {
      const eventToSend = this.queue.shift();

      if (eventToSend) {
        const {
          eventName,
          properties,
          sendAsBeacon = false
        } = eventToSend;

        this._track({
          eventName,
          properties,
          sendAsBeacon
        });
      }
    }
  },

  enableTrackingByEmailConsent(email) {
    this.setProperties({
      email
    });
    this.isEmailProvided = true;

    if (!this.identifiableTrackingEnabled) {
      this.identifiableTrackingEnabled = true;
      this.setProperties(Object.assign({}, !this.isDeviceIdProvided && {
        deviceId: createFreshUuid()
      }, {
        hstc: hasAcceptedCookies() ? getTrackerHstc : null
      }));
    }

    this.drainQueue();
    this.track('interaction', {
      action: 'tracking-enabled-by-email'
    });
  },

  enableTrackingByCookieConsent() {
    this.setProperties({
      hstc: getTrackerHstc
    });

    if (!this.identifiableTrackingEnabled) {
      this.identifiableTrackingEnabled = true;
      this.setProperties(Object.assign({}, !this.isDeviceIdProvided && {
        deviceId: createFreshUuid()
      }));
    }

    this.areCookiesAccepted = true;
    this.drainQueue();
    this.track('interaction', {
      action: 'tracking-enabled-by-cookie-consent'
    });
  },

  enableTrackingByPortalId() {
    this.isPortalIdProvided = true;

    if (!this.identifiableTrackingEnabled) {
      this.identifiableTrackingEnabled = true;
      this.setProperties(Object.assign({}, !this.isDeviceIdProvided && {
        deviceId: createFreshUuid()
      }, {
        hstc: hasAcceptedCookies() ? getTrackerHstc : null
      }));
    }
  },

  setProperties(properties) {
    if (properties.hubId) {
      this.enableTrackingByPortalId();
    }

    const hubId = parseInt(properties.hubId, 10);
    const propertiesToSet = Object.assign({}, properties, {}, properties.hubId && {
      hubId
    }, {}, properties.email && !getUtk() && {
      hstc: null
    });
    this.get().setProperties(propertiesToSet);
  },

  set(trackerInstance) {
    this.TRACKER = trackerInstance;
  },

  /**
   * Internal track function that chooses the right tracker before
   * calling the track method.
   *
   * @param {String} eventName The event name.
   * @param {Object} properties The properties object.
   * @param {boolean} [sendAsBeacon=false]
   */
  _track({
    eventName,
    properties = {},
    sendAsBeacon = false
  }) {
    const tracker = sendAsBeacon ? this.getBeaconTracker() : this.getHttpTracker();
    tracker.track(eventName, properties);
  }

});
const UsageTrackerContainer = getUsageTrackerContainer();
export default UsageTrackerContainer;